/**
 * ActionButton Component
 *
 * Generic action button renderer for both primary and secondary actions.
 */

import React from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DetailAction, BaseEntity } from "../types/detailPage";
import { getActionVariant } from "../types/action-rendering";
import {
  INTENT_BUTTON_CLASSES,
  resolveActionIntent,
} from "@/components/shared/EntityActionBar/action-intent";

interface ActionButtonProps<T extends BaseEntity> {
  action: DetailAction<T>;
  isLoading: boolean;
  isDisabled: boolean;
  label: string;
  onClick: () => void;
  isDropdown?: boolean;
}

const ActionButtonBase = React.forwardRef<
  HTMLButtonElement,
  ActionButtonProps<any>
>(
  (
    {
      action,
      isLoading,
      isDisabled,
      label,
      onClick,
      isDropdown = false,
    },
    ref
  ) => {
    // One sentiment vocabulary across detail pages and action bars: the intent
    // decides both the variant and the tint, instead of colours being retyped
    // per action (and usually missing dark mode).
    const intent = resolveActionIntent(action.id, action.intent);
    const variant = getActionVariant(action.id, action.intent);

    return (
      <Button
        ref={ref}
        variant={variant}
        data-intent={intent}
        size={isDropdown ? "sm" : "default"}
        disabled={isDisabled || isLoading}
        onClick={onClick}
        className={cn(
          INTENT_BUTTON_CLASSES[intent],
          action.className || "",
          isDropdown ? "w-full justify-start" : "",
          !isDropdown && "min-h-[44px] touch-manipulation active:scale-[0.98]"
        )}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <span>{label}</span>
        )}
      </Button>
    );
  }
);

ActionButtonBase.displayName = "ActionButton";

export const ActionButton = React.memo(ActionButtonBase);
